/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { useBrandMetrics } from '../../hooks/useBrandMetrics';
import BrandSwitcher from '../../components/BrandSwitcher';
import OrgSwitcher from '../../components/OrgSwitcher';
import DemographicFilter from '../../components/DemographicFilter';
import ExportMenu from '../../components/ExportMenu';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { useFilters } from '../../contexts/FiltersContext';
import { useEffect } from 'react';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

/**
 * Main Dashboard Page with Traditional, Digital, and Combined views
 */
const DashboardLayout = () => {
  const navigate = useNavigate();
  const { allOrgs, selectedOrg, setSelectedOrg, allBrands, selectedBrand, setSelectedBrand, allFilters, setSelectedFilters, loading, error } = useFilters()
  // const { selectedDate, getMonthFromDate } = useDateFilter();
  const { health, /* allBrands, demographicFilters: allFilters, digitalMetrics, */ loading: metricsLoading, error: metricsError } = useBrandMetrics();

  // useEffect(() => console.log(selectedBrand, metrics, metricsLoading), [selectedBrand, metrics, metricsLoading])

  const handleAskBrandDoctor = () => {
    const metrics = health?.traditional
    if (metrics) {
      // Build comprehensive context for the Brand Doctor
      const contextParts = [];
      
      // Add key metrics
      contextParts.push(`Brand: ${metrics.name}`);
      contextParts.push(`Awareness: ${metrics.metrics.awareness.toFixed(1)}%`);
      contextParts.push(`Consideration: ${metrics.metrics.consideration.toFixed(1)}%`);
      contextParts.push(`Usage: ${metrics.metrics.usage.toFixed(1)}%`);
      contextParts.push(`NPS Score: ${metrics.metrics.nps}`);
      contextParts.push(`Quality Perception: ${metrics.metrics.quality.toFixed(1)}%`);
      contextParts.push(`Trust: ${metrics.metrics.trust.toFixed(1)}%`);
      contextParts.push(`Value: ${metrics.metrics.value.toFixed(1)}%`);
      
      // Add insights about performance
      if (metrics.metrics.nps > 50) {
        contextParts.push('Strong NPS indicates high advocacy potential');
      } else if (metrics.metrics.nps < 0) {
        contextParts.push('Negative NPS suggests urgent attention needed');
      }
      
      const prompt = `Analyze the brand health for ${metrics.name}:\n\n${contextParts.join('\n')}\n\nProvide strategic insights and actionable recommendations to improve brand performance.`;
      
      navigate('/', { 
        state: { 
          initialMessage: prompt,
          brandId: selectedBrand,
          brandMetrics: metrics 
        }
      });
    }
  };

  // Show loading state while data is being fetched
  if (!selectedBrand || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-text-muted mt-4">Loading brands data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <i className="fas fa-exclamation-triangle text-4xl text-red-400 mb-4"></i>
          <p className="text-text-muted">Error loading data: {error.message}</p>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen p-6 bg-background">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Brand Health Dashboard</h1>
            <p className="text-text-muted">Real-time brand performance metrics from surveys and social media</p>
          </div>
          <div className="flex items-center space-x-3">
            <OrgSwitcher
              orgs={allOrgs}
              selectedOrg={selectedOrg}
              onOrgSelect={setSelectedOrg}
            />
            {/* Elegant Brand Switcher */}
            <BrandSwitcher
              brands={allBrands}
              selectedBrand={selectedBrand}
              onBrandSelect={setSelectedBrand}
              recentBrands={[]}
            />

            {/* Demographic Filters */}
            <DemographicFilter availableFilters={allFilters} onFiltersChange={setSelectedFilters} />

            {/* Export Menu */}
            {health && (
              <ExportMenu 
                data={health}
                dashboardElementId="dashboard-content"
                brandName={health.traditional.name}
                exportType="all"
                filename={`${health.traditional.name.toLowerCase().replace(/\s+/g, '_')}_brand_health`}
              />
            )}

            {/* Ask Brand Doctor Button */}
            <button
              onClick={handleAskBrandDoctor}
              className="flex items-center gap-1.5 px-2.5 py-1 border border-border/50 rounded-full hover:border-primary/40 hover:bg-primary/5 transition-all"
            >
              <i className="fas fa-stethoscope text-text-muted text-xs"></i>
              <span className="text-foreground text-sm">Ask Brand Doctor</span>
            </button>
          </div>
        </div>
      </div>


      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-8 border-b border-border overflow-x-auto">
        <NavLink to="/dashboard/executive" className={({isActive}) => `px-4 py-3 font-medium transition-all whitespace-nowrap relative ${isActive? 'text-primary' : 'text-text-muted hover:text-foreground' }`}>
          {({isActive}) => (
            <>
              <i className="fas fa-chart-line mr-2"></i>
              Executive Summary
              {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
            </>
          )}
        </NavLink>
        <NavLink to="/dashboard/deepdive" className={({isActive}) => `px-4 py-3 font-medium transition-all whitespace-nowrap relative ${isActive? 'text-green-600 dark:text-green-400' : 'text-text-muted hover:text-foreground' }`}>
          {({isActive}) => (
            <>
              <i className="fas fa-microscope mr-2"></i>
              Traditional Health
              {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600 dark:bg-green-400 rounded-full" />}
            </>
          )}
        </NavLink>
        <NavLink to="/dashboard/digital" className={({isActive}) => `px-4 py-3 font-medium transition-all whitespace-nowrap relative ${isActive? 'text-cyan-600 dark:text-cyan-400' : 'text-text-muted hover:text-foreground' }`}>
          {({isActive}) => (
            <>
              <i className="fas fa-globe mr-2"></i>
              Digital Health
              {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-600 dark:bg-cyan-400 rounded-full" />}
            </>
          )}
        </NavLink>
        <NavLink to="/dashboard/comparison" className={({isActive}) => `px-4 py-3 font-medium transition-all whitespace-nowrap relative ${isActive? 'text-purple-600 dark:text-purple-400' : 'text-text-muted hover:text-foreground' }`}>
          {({isActive}) => (
            <>
              <i className="fas fa-chart-bar mr-2"></i>
              Comparison
              {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600 dark:bg-purple-400 rounded-full" />}
            </>
          )}
        </NavLink>
        <NavLink to="/dashboard/posts" className={({isActive}) => `px-4 py-3 font-medium transition-all whitespace-nowrap relative ${isActive? 'text-orange-600 dark:text-orange-400' : 'text-text-muted hover:text-foreground' }`}>
          {({isActive}) => (
            <>
              <i className="fas fa-newspaper mr-2"></i>
              Posts
              {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600 dark:bg-orange-400 rounded-full" />}
            </>
          )}
        </NavLink>
      </div>

      {/* Content based on active tab */}
      {(!health || metricsLoading)? (
          <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <p className="text-text-muted mt-4">Loading brands data...</p>
            </div>
          </div>
      ): metricsError? (
          <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="text-center">
              <i className="fas fa-exclamation-triangle text-4xl text-red-400 mb-4"></i>
              <p className="text-text-muted">Error loading data: {error}</p>
            </div>
          </div>
      ): (
        <div id="dashboard-content">
          <Outlet />
          {/* metrics && (
            <>
              {activeTab === 'executive' && (
              
            )}

            {activeTab === 'deepdive' && (
              <DeepDiveDashboard 
                brandId={selectedBrand}
                brandName={metrics.name}
                metrics={metrics.metrics}
                demographicFilters={demographicFilters}
              />
            )}

            {activeTab === 'digital' && metrics.digital && (
              <DigitalMetricsDashboard 
                metrics={metrics.digital} 
                brandName={metrics.name} 
              />
            )}
          </>
        ) */}

        {/* activeTab === 'comparison' && (
          <BrandComparison />
        )}

        {activeTab === 'posts' && metrics && (
          <PostsDashboard
            brandId={selectedBrand}
            brandName={metrics.name}
          />
        ) */}
        </div>
      )}
    </div>
  );
};

export default DashboardLayout;
